/**
 * Destination when a globally signed-in user hits a guest-only route.
 *
 * Uses peek APIs (not consume) so React Strict Mode / remounts cannot wipe
 * a pending post-login return target. Accidental visits to /sign-in while
 * already signed in should not steal return-to-origin storage.
 *
 * Wave C — post-auth consume lands orders intent on `/account/orders`. Guest
 * bounce peek uses the same destination (peek only — do not consume).
 * Wave D — BottomNav signed-in unresolved-home click recovery also uses
 * `ORDERS_INTENT_PLATFORM_RECOVERY_PATH` (`/account/orders`).
 */

import { resolveDefaultPostAuthLandingPath } from '../../shop/resolveShopHref.js';
import {
  isAuthLoopReturnPath,
  isMapResumeReturnPath,
  isPaymentResumeReturnPath,
  peekInMemoryReturnTarget,
  peekMapResumeReturnTarget,
  peekPaymentResumeReturnTarget,
  peekPlatformReturnTarget,
  peekPostAuthNavigateHandoff,
  peekPostAuthOrdersIntent,
  peekReturnTarget,
  toPlatformChromeEmitPath,
} from '../../../shared/session/returnTarget.js';

/** Platform `/account/orders` — Wave C/D post-auth + unresolved-home recovery. */
const PLATFORM_ACCOUNT_ORDERS_PATH = '/account/orders';

/**
 * BottomNav signed-in unresolved-home Objednávky recovery (Wave D / Wave C closer).
 * `/account/orders` (never `/favorites` or `/sign-in`).
 */
export const ORDERS_INTENT_PLATFORM_RECOVERY_PATH = PLATFORM_ACCOUNT_ORDERS_PATH;

/** G2 — SoftRace handoff/parks must never bounce guest-auth back onto /sign-in. */
function emitSafeGuestRedirect(path: string, tenantCode: string): string {
  const emitted = toPlatformChromeEmitPath(path);
  if (isAuthLoopReturnPath(emitted) || isAuthLoopReturnPath(path)) {
    return resolveDefaultPostAuthLandingPath(tenantCode);
  }
  return emitted;
}

function peekPostAuthReturnTarget(tenantCode: string): string {
  // G2 — consume-once handoff from navigateAfterAuth / OIDC restore.
  const handoff = peekPostAuthNavigateHandoff();
  if (handoff !== null) {
    return emitSafeGuestRedirect(handoff, tenantCode);
  }
  const memory = peekInMemoryReturnTarget();
  if (memory !== null) {
    return emitSafeGuestRedirect(memory, tenantCode);
  }
  // SoftRace after handoff microtask: durable payment-resume must beat
  // leftover orders/platform and Stage 3 hub (empty cart without kioskId).
  const paymentResume = peekPaymentResumeReturnTarget();
  if (paymentResume !== null) {
    return emitSafeGuestRedirect(paymentResume, tenantCode);
  }
  // G1 — leftover checkout twin must not beat durable map after map elevate
  // cleared payment durable but (historically) left customer:returnTo:{tenant}.
  const mapResumeEarly = peekMapResumeReturnTarget();
  if (
    tenantCode.trim().length > 0 &&
    tenantCode !== '__platform__' &&
    !tenantCode.startsWith('__') &&
    mapResumeEarly === null
  ) {
    const perTenantEarly = peekReturnTarget({ tenantCode });
    if (
      perTenantEarly !== null &&
      isPaymentResumeReturnPath(perTenantEarly)
    ) {
      return emitSafeGuestRedirect(perTenantEarly, tenantCode);
    }
  }
  const mapResume = mapResumeEarly;
  if (mapResume !== null) {
    return emitSafeGuestRedirect(mapResume, tenantCode);
  }
  if (
    tenantCode.trim().length > 0 &&
    tenantCode !== '__platform__' &&
    !tenantCode.startsWith('__')
  ) {
    const perTenantMap = peekReturnTarget({ tenantCode });
    if (perTenantMap !== null && isMapResumeReturnPath(perTenantMap)) {
      return emitSafeGuestRedirect(perTenantMap, tenantCode);
    }
  }
  if (peekPostAuthOrdersIntent()) {
    return PLATFORM_ACCOUNT_ORDERS_PATH;
  }
  const platform = peekPlatformReturnTarget();
  if (platform !== null) {
    return emitSafeGuestRedirect(platform, tenantCode);
  }
  if (
    tenantCode.trim().length > 0 &&
    tenantCode !== '__platform__' &&
    !tenantCode.startsWith('__')
  ) {
    const perTenant = peekReturnTarget({ tenantCode });
    if (perTenant !== null) {
      return emitSafeGuestRedirect(perTenant, tenantCode);
    }
  }
  return resolveDefaultPostAuthLandingPath(tenantCode);
}

export function resolveGuestAuthRedirectTarget(tenantCode: string): string {
  return peekPostAuthReturnTarget(tenantCode);
}
